import { Badge } from '@optools/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@optools/components/ui/dialog';
import { Separator } from '@optools/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@optools/components/ui/table';
import { AuditView } from '@optools/services/operator-gateway/types/generated/graphql';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { startCase } from 'es-toolkit';
import { Tags } from 'lucide-react';

import { AuditTableDialogType } from '../../schemas/audits';

export interface AuditTableResourceTagsProps {
  auditId: number;
  operation: string;
  tags: AuditView['resourceTags'];
}

export function AuditTableResourceTags({ auditId, operation, tags }: AuditTableResourceTagsProps) {
  const params = useSearch({ from: '/_auth/audit/audits' });
  const navigate = useNavigate();

  const handleOpenChange = (open: boolean) => {
    navigate({
      search: (current) => ({
        ...current,
        auditId: open ? auditId : undefined,
        dialog: open ? AuditTableDialogType.ResourceTags : undefined,
      }),
      to: '/audit/audits',
    });
  };

  if (!tags?.length) return null;

  if (tags.length > 2) {
    const open = params.auditId === auditId && params.dialog === AuditTableDialogType.ResourceTags;
    return (
      <Dialog defaultOpen={open} open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger>
          <Badge variant="secondary">
            <Tags />
            {tags?.length} tags
          </Badge>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resource Tags</DialogTitle>
            <DialogDescription>{operation}</DialogDescription>
          </DialogHeader>
          <div className="max-h-80">
            <Table containerClassName="h-full relative">
              <TableHeader className="sticky top-0 border-b bg-background">
                <TableRow>
                  <TableHead className="px-4">Name</TableHead>
                  <TableHead className="px-4">Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tags.map((tag) => (
                  <TableRow key={tag.value}>
                    <TableCell className="p-4 whitespace-nowrap">{startCase(tag.name)}</TableCell>
                    <TableCell className="p-4 whitespace-nowrap">{tag.value}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <ul className="flex flex-wrap gap-2">
      {tags?.map((tag) => (
        <li key={tag.value}>
          <Badge variant="secondary">
            <span>{startCase(tag.name)}</span>
            <Separator className="w-0.5!" orientation="vertical" />
            <span>{tag.value}</span>
          </Badge>
        </li>
      ))}
    </ul>
  );
}
